/**
 * Inline manual entry for coverage requirement report fields — saves via
 * the same authored-evidence path as AuthorEvidenceDialog.
 */
import React from 'react';
import type { ChecklistItem } from '../../../config/documentChecklists';
import type { ExtractedClaim } from '../../../types/siteContext';
import {
  inferAuthoredConfidence,
  isFieldFilled,
  isAuthoredValuePresent,
  makeEmptyForm,
  type AuthoredEvidenceFormState,
} from '../../../types/authoredEvidence';
import { writeAuthoredClaim } from '../../../services/siteContext/authoredEvidenceWriter';
import { AuthoredFieldInput } from './AuthoredFieldInput';
import type { FieldPresentationHint } from '../../../lib/coverage/resolveRequirementFieldPresentation';

export interface RequirementInlineAuthorFormProps {
  siteId: string;
  item: ChecklistItem;
  existingClaim?: ExtractedClaim;
  /** Schema keys to render editors for (typically empty primary fields). */
  fieldKeys: readonly string[];
  /** Context-aware intro + per-field guidance from report type / intent. */
  sectionIntro?: string;
  fieldHints?: Readonly<Record<string, FieldPresentationHint>>;
  /** Edit one displayed fact without repeating the whole worksheet. */
  singleField?: boolean;
  initialValues?: Readonly<Record<string, string | number | boolean>>;
  onCancel?: () => void;
  onSaved?: (result: { claimId: string; requirementId: string }) => void;
}

function seedForm(
  existing: ExtractedClaim | undefined,
  schema: ChecklistItem['authoredSchema'],
  singleField = false,
): AuthoredEvidenceFormState {
  // A single-fact edit must not author default false values for untouched
  // boolean siblings. Retain only facts already explicitly authored.
  const base = makeEmptyForm(singleField ? [] : schema ?? []);
  if (!existing?.structuredFields) return base;
  for (const f of schema ?? []) {
    if (f.key === 'notes') continue;
    const v = existing.structuredFields[f.key];
    if (v !== undefined) base.fields[f.key] = v;
  }
  const source = existing.sourceSpan ?? existing.claim ?? '';
  const notesIdx = source.indexOf('Notes: ');
  if (typeof existing.structuredFields.notes === 'string') {
    base.notes = existing.structuredFields.notes;
  } else if (notesIdx >= 0) {
    base.notes = source.slice(notesIdx + 'Notes: '.length).trim();
  }
  return base;
}

export const RequirementInlineAuthorForm: React.FC<RequirementInlineAuthorFormProps> = ({
  siteId,
  item,
  existingClaim,
  fieldKeys,
  sectionIntro,
  fieldHints,
  singleField = false,
  initialValues,
  onCancel,
  onSaved,
}) => {
  const formId = React.useId();
  const schema = item.authoredSchema ?? [];
  const editableFields = schema
    .filter(f => f.key !== 'notes' && fieldKeys.includes(f.key))
    .sort((a, b) => {
      const pa = fieldHints?.[a.key]?.priority ?? 100;
      const pb = fieldHints?.[b.key]?.priority ?? 100;
      return pa - pb || a.key.localeCompare(b.key);
    });
  const notesField = schema.find(f => f.key === 'notes' && (!singleField || fieldKeys.includes('notes')));
  const seededForm = () => {
    const next = seedForm(existingClaim, schema, singleField);
    for (const key of fieldKeys) {
      const value = initialValues?.[key];
      if (value === undefined) continue;
      if (key === 'notes') next.notes = String(value);
      else next.fields[key] = value;
    }
    return next;
  };

  const [form, setForm] = React.useState<AuthoredEvidenceFormState>(() =>
    seededForm(),
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    setForm(seededForm());
    setDirty(false);
    setError(null);
  }, [item.id, existingClaim?.id, existingClaim?.lastWrittenAt]);

  if (editableFields.length === 0 && !notesField) return null;

  const inferred = inferAuthoredConfidence(form, schema);
  const hasDraft =
    editableFields.some(f => singleField ? isAuthoredValuePresent(form.fields[f.key]) : isFieldFilled(form.fields[f.key]))
    || isFieldFilled(form.notes);

  const setFieldValue = (key: string, value: string | number | boolean) => {
    setForm(f => ({ ...f, fields: { ...f.fields, [key]: value } }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!hasDraft || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await writeAuthoredClaim({
        siteId,
        requirementId: item.id,
        requirementLabel: item.label,
        schema,
        form: { ...form, confidence: inferred },
      });
      setDirty(false);
      onSaved?.({ claimId: result.claimId, requirementId: item.id });
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={singleField ? 'space-y-2' : 'rounded-sm border border-border-subtle bg-surface-1/60 p-2.5 space-y-2'}
      data-testid="coverage-requirement-inline-author"
    >
      {!singleField && <p
        className="text-[10px] text-text-muted leading-snug"
        data-testid="coverage-inline-author-intro"
      >
        {sectionIntro ?? 'Enter verified values manually, or upload a supporting document when available.'}
      </p>}

      <ul className="space-y-2">
        {editableFields.map(field => {
          const hint = fieldHints?.[field.key];
          const inputId = `${formId}-author-evidence-field-${field.key}`;
          return (
          <li
            key={field.key}
            className={singleField ? 'space-y-1' : 'space-y-1 rounded-sm border border-border-subtle/60 bg-background/40 p-2'}
            data-testid={`coverage-inline-field-row-${field.key}`}
            data-recommended={hint?.recommended ? 'true' : 'false'}
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <label
                htmlFor={inputId}
                className="text-[10px] font-medium text-text-secondary"
              >
                {field.label}
                {field.unit ? ` (${field.unit})` : ''}
              </label>
              {!singleField && hint?.recommended && (
                <span
                  className="text-[9px] uppercase tracking-wide text-primary font-semibold"
                  data-testid={`coverage-inline-field-recommended-${field.key}`}
                >
                  Recommended
                </span>
              )}
            </div>
            {hint?.guidance && (
              <p
                className="text-[10px] text-text-muted leading-snug"
                data-testid={`coverage-inline-field-guidance-${field.key}`}
              >
                {hint.guidance}
              </p>
            )}
            {!singleField && hint?.proxyHint && (
              <p
                className="text-[10px] text-text-muted/90 italic"
                data-testid={`coverage-inline-field-proxy-${field.key}`}
              >
                {hint.proxyHint.label}: {hint.proxyHint.displayValue}
                {' '}
                <span className="not-italic">({hint.proxyHint.sourceLabel})</span>
              </p>
            )}
            {singleField && field.type === 'boolean' ? <select id={inputId}
              className="w-full rounded-sm border border-border bg-background px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid={`coverage-inline-field-input-${field.key}`}
              value={form.fields[field.key] === true ? 'yes' : form.fields[field.key] === false ? 'no' : ''}
              onChange={event => setFieldValue(field.key, event.target.value === '' ? '' : event.target.value === 'yes')}>
              <option value="">Unknown</option><option value="yes">Yes</option><option value="no">No</option>
            </select> : <AuthoredFieldInput
              inputId={inputId}
              field={field}
              value={form.fields[field.key]}
              onChange={(v) => setFieldValue(field.key, v)}
              compact
            />}
          </li>
          );
        })}
      </ul>

      {notesField && (
        <div className="space-y-1">
          <label htmlFor={`${formId}-notes`} className="text-[10px] text-text-muted">
            {notesField.label}
          </label>
          <textarea
            id={`${formId}-notes`}
            data-testid="coverage-inline-notes"
            className="w-full min-h-[52px] text-[10px] border border-border bg-background px-1.5 py-1 rounded-sm focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder={notesField.placeholder}
            value={form.notes}
            onChange={(e) => {
              setForm(f => ({ ...f, notes: e.target.value }));
              setDirty(true);
            }}
          />
        </div>
      )}

      {error && (
        <p className="text-[10px] text-danger" data-testid="coverage-inline-author-error">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && <button type="button" onClick={onCancel} disabled={saving}
          className="rounded-sm px-2.5 py-1 text-[10px] text-text-muted hover:text-text-primary focus-visible:outline focus-visible:outline-primary">Cancel</button>}
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasDraft || saving || (singleField && !dirty)}
          className="rounded-sm bg-primary text-primary-foreground px-2.5 py-1 text-[10px] font-semibold hover:opacity-90 disabled:opacity-50"
          data-testid="coverage-inline-author-save"
        >
          {saving ? 'Saving…' : singleField ? 'Save fact' : 'Save values'}
        </button>
      </div>
    </div>
  );
};
